/**
 * Dados do chat entre pessoas (`/chat`, ADR-0017): leituras e escritas, todas pelo cliente tipado.
 *
 * Enviar, editar, eliminar e marcar como lida são otimistas: mostram-se já e acertam com a resposta. Um envio leva uma
 * chave de cliente, por isso um reenvio (automático ou «tentar de novo») nunca duplica no servidor.
 */

import { useCallback } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import { enqueuePending, newClientKey, readOutbox, removePending } from "@/lib/chat/local-store";
import {
  emptyWindow,
  markDeleted,
  markFailed,
  optimisticMessage,
  prependOlder,
  upsertMessage,
  type ChatMessage,
  type MessageWindow,
} from "@/lib/chat/message-window";
import { applyRead, type ChatOverview } from "@/lib/chat/sidebar";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import type { Schemas } from "@/types/api";
import { chatKeys, CHAT_ROOT } from "./people-chat-keys";

export type ConversationDetail = Schemas["ChatConversationDetail"];
export type ChatMember = Schemas["ChatMemberRead"];

const PAGE = 50;

export function useChatOverview(enabled = true) {
  return useQuery({ queryKey: chatKeys.overview(), queryFn: () => api.get("/chat/overview"), enabled, staleTime: 30_000 });
}

export function useConversationDetail(conversationId: string | null) {
  return useQuery({
    queryKey: chatKeys.conversation(conversationId ?? ""),
    queryFn: () => api.get("/chat/conversations/{conversation_id}", { path: { conversation_id: conversationId ?? "" } }),
    enabled: !!conversationId,
    staleTime: 60_000,
  });
}

async function fetchWindow(conversationId: string, threadRootId: string | null): Promise<MessageWindow> {
  const page = await api.get("/chat/conversations/{conversation_id}/messages", {
    path: { conversation_id: conversationId },
    query: { limit: PAGE, thread_root_id: threadRootId ?? undefined },
  });
  return { items: page.items, hasMoreBefore: page.has_more_before, firstUnreadSeq: page.first_unread_seq ?? null };
}

/** As mensagens de uma conversa (ou thread). Depois da primeira carga, só os eventos a alteram: sem refetch periódico. */
export function useMessageWindow(conversationId: string | null, threadRootId: string | null = null) {
  return useQuery({
    queryKey: chatKeys.window(conversationId ?? "", threadRootId),
    queryFn: () => fetchWindow(conversationId ?? "", threadRootId),
    enabled: !!conversationId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** As conversas mais prováveis abrem-se já em cache (ao passar o rato ou focar na barra lateral). */
export function prefetchConversation(qc: QueryClient, conversationId: string): void {
  void qc.prefetchQuery({ queryKey: chatKeys.window(conversationId, null), queryFn: () => fetchWindow(conversationId, null), staleTime: Infinity });
}

export function useLoadOlder(conversationId: string, threadRootId: string | null) {
  const qc = useQueryClient();
  return useCallback(async () => {
    const key = chatKeys.window(conversationId, threadRootId);
    const current = qc.getQueryData<MessageWindow>(key);
    const first = current?.items.find((m) => m.seq > 0)?.seq;
    if (!current?.hasMoreBefore || !first) return;
    const page = await api.get("/chat/conversations/{conversation_id}/messages", {
      path: { conversation_id: conversationId },
      query: { limit: PAGE, before_seq: first, thread_root_id: threadRootId ?? undefined },
    });
    qc.setQueryData<MessageWindow>(key, (w) => prependOlder(w ?? emptyWindow(), page));
  }, [qc, conversationId, threadRootId]);
}

export function useSendMessage(me: ChatMessage["author"]) {
  const qc = useQueryClient();
  return useCallback(
    async (args: {
      conversationId: string;
      content: string;
      threadRootId: string | null;
      /** A mensagem a que se responde (citação ao estilo do Instagram). */
      replyTo?: ChatMessage["reply_to"];
      clientKey?: string;
      attachments?: ChatMessage["attachments"];
      mentions?: Schemas["ChatMentionInput"][];
    }) => {
      const clientKey = args.clientKey ?? newClientKey();
      const key = chatKeys.window(args.conversationId, args.threadRootId);
      const pending = optimisticMessage({
        conversationId: args.conversationId,
        clientKey,
        content: args.content,
        threadRootId: args.threadRootId,
        replyTo: args.replyTo,
        author: me,
        attachments: args.attachments,
      });
      qc.setQueryData<MessageWindow>(key, (w) => upsertMessage(w ?? emptyWindow(), pending));
      enqueuePending({
        conversationId: args.conversationId,
        clientKey,
        content: args.content,
        threadRootId: args.threadRootId,
        replyToId: args.replyTo?.id ?? null,
        createdAt: pending.created_at,
        attachments: args.attachments ?? [],
        mentions: args.mentions ?? [],
      });
      try {
        const saved = await api.post("/chat/conversations/{conversation_id}/messages", {
          path: { conversation_id: args.conversationId },
          body: {
            content: args.content,
            client_key: clientKey,
            thread_root_id: args.threadRootId,
            reply_to_id: args.replyTo?.id ?? null,
            attachment_ids: (args.attachments ?? []).map((a) => a.id),
            mentions: args.mentions ?? [],
          },
        });
        removePending(clientKey);
        qc.setQueryData<MessageWindow>(key, (w) => upsertMessage(w ?? emptyWindow(), saved));
        qc.setQueryData<ChatOverview>(chatKeys.overview(), (o) => (o ? applyRead(o, args.conversationId, saved.seq) : o));
        return saved;
      } catch (error) {
        qc.setQueryData<MessageWindow>(key, (w) => markFailed(w ?? emptyWindow(), clientKey));
        throw error;
      }
    },
    [qc, me],
  );
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutationWithToast<ChatMessage, Error, { conversationId: string; messageId: string; content: string; threadRootId: string | null }>({
    mutationFn: ({ conversationId, messageId, content }) =>
      api.patch("/chat/conversations/{conversation_id}/messages/{message_id}", {
        path: { conversation_id: conversationId, message_id: messageId },
        body: { content },
      }),
    onSuccessCallback: (saved, vars) => {
      qc.setQueryData<MessageWindow>(chatKeys.window(vars.conversationId, vars.threadRootId), (w) => upsertMessage(w ?? emptyWindow(), saved));
    },
    errorMessage: "Não foi possível editar a mensagem.",
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutationWithToast<ChatMessage, Error, { conversationId: string; messageId: string; threadRootId: string | null }>({
    mutationFn: ({ conversationId, messageId }) =>
      api.delete("/chat/conversations/{conversation_id}/messages/{message_id}", { path: { conversation_id: conversationId, message_id: messageId } }),
    mutationOptions: {
      onMutate: (vars) => {
        const key = chatKeys.window(vars.conversationId, vars.threadRootId);
        const previous = qc.getQueryData<MessageWindow>(key);
        qc.setQueryData<MessageWindow>(key, (w) => markDeleted(w ?? emptyWindow(), vars.messageId));
        return { previous };
      },
    },
    // O servidor recusou: a mensagem volta a aparecer como estava.
    onErrorCallback: (_error, vars, context) => {
      const previous = (context as { previous?: MessageWindow } | undefined)?.previous;
      if (previous) qc.setQueryData<MessageWindow>(chatKeys.window(vars.conversationId, vars.threadRootId), previous);
    },
    errorMessage: "Não foi possível eliminar a mensagem.",
  });
}

export function usePinMessage() {
  const qc = useQueryClient();
  return useMutationWithToast<ChatMessage, Error, { conversationId: string; messageId: string; pinned: boolean; threadRootId: string | null }>({
    mutationFn: ({ conversationId, messageId, pinned }) =>
      api.put("/chat/conversations/{conversation_id}/messages/{message_id}/pin", {
        path: { conversation_id: conversationId, message_id: messageId },
        body: { pinned },
      }),
    onSuccessCallback: (saved, vars) => {
      qc.setQueryData<MessageWindow>(chatKeys.window(vars.conversationId, vars.threadRootId), (w) => upsertMessage(w ?? emptyWindow(), saved));
    },
  });
}

// O último `seq` que o SERVIDOR já sabe que foi lido, por conversa. O contador local pode subir antes (a conversa está
// aberta quando a mensagem chega): não pode ser ele a decidir se o pedido segue, senão a leitura nunca chegava lá.
const acknowledged = new Map<string, number>();

const inFlight = new Set<string>();

/** Reenvia o que ficou por enviar (depois de recarregar a página, ou quando a ligação volta). Idempotente no servidor. */
export async function replayOutbox(qc: QueryClient, only?: string): Promise<void> {
  for (const item of readOutbox()) {
    if ((only && item.clientKey !== only) || inFlight.has(item.clientKey)) continue;
    inFlight.add(item.clientKey);
    const key = chatKeys.window(item.conversationId, item.threadRootId);
    try {
      const saved = await api.post("/chat/conversations/{conversation_id}/messages", {
        path: { conversation_id: item.conversationId },
        body: {
          content: item.content,
          client_key: item.clientKey,
          thread_root_id: item.threadRootId,
          reply_to_id: item.replyToId ?? null,
          attachment_ids: ((item.attachments ?? []) as ChatMessage["attachments"]).map((a) => a.id),
          mentions: (item.mentions ?? []) as Schemas["ChatMentionInput"][],
        },
      });
      removePending(item.clientKey);
      qc.setQueryData<MessageWindow>(key, (w) => (w ? upsertMessage(w, saved) : w));
    } catch (error) {
      qc.setQueryData<MessageWindow>(key, (w) => (w ? markFailed(w, item.clientKey) : w));
      // Recusada de vez (sem acesso, conversa apagada): sai da fila; sem rede: fica para a próxima.
      const status = (error as { status?: number }).status;
      if (status && status >= 400 && status < 500) removePending(item.clientKey);
    } finally {
      inFlight.delete(item.clientKey);
    }
  }
}

/** Marcar como lida: o contador baixa já; o pedido segue atrás (é idempotente e nunca recua). */
export function useMarkRead() {
  const qc = useQueryClient();
  return useCallback(
    (conversationId: string, seq: number) => {
      const overview = qc.getQueryData<ChatOverview>(chatKeys.overview());
      const summary = overview?.conversations.find((c) => c.id === conversationId);
      if (!summary?.is_member) return;
      const known = acknowledged.get(conversationId) ?? 0;
      if (seq <= known) return;
      acknowledged.set(conversationId, seq);
      qc.setQueryData<ChatOverview>(chatKeys.overview(), (o) => (o ? applyRead(o, conversationId, seq) : o));
      void api.put("/chat/conversations/{conversation_id}/read", { path: { conversation_id: conversationId }, body: { seq } }).catch(() => undefined);
    },
    [qc],
  );
}

export function useMembers(conversationId: string | null) {
  return useQuery({
    queryKey: chatKeys.members(conversationId ?? ""),
    queryFn: () => api.get("/chat/conversations/{conversation_id}/members", { path: { conversation_id: conversationId ?? "" } }),
    enabled: !!conversationId,
  });
}

export function useStructureLog(conversationId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: chatKeys.log(conversationId ?? ""),
    queryFn: () => api.get("/chat/conversations/{conversation_id}/log", { path: { conversation_id: conversationId ?? "" } }),
    enabled: !!conversationId && enabled,
  });
}

export function useChannelDirectory(q: string, includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: chatKeys.channels(q, includeArchived),
    enabled,
    queryFn: () => api.get("/chat/channels", { query: { q: q || undefined, include_archived: includeArchived } }),
    // Um canal criado por outra pessoa não chega pelo tempo real a quem ainda não é membro: abrir o diretório pergunta sempre.
    staleTime: 0,
  });
}

/** Depois de uma mudança de estrutura: a barra lateral e o detalhe voltam a ler-se (não é por mensagem). */
export function refreshStructure(qc: QueryClient, conversationId?: string): void {
  void qc.invalidateQueries({ queryKey: chatKeys.overview() });
  void qc.invalidateQueries({ queryKey: [...CHAT_ROOT, "channels"] });
  if (conversationId) {
    void qc.invalidateQueries({ queryKey: chatKeys.conversation(conversationId) });
    void qc.invalidateQueries({ queryKey: chatKeys.members(conversationId) });
    void qc.invalidateQueries({ queryKey: chatKeys.log(conversationId) });
  }
}
